import React from 'react';
import { escapeRegExp, get, groupBy, isObject, mapValues, orderBy, some, sortBy, startCase, words } from 'lodash-es';
import {
  ACTION_STAGE_MAP,
  ALL_STAGES,
  DEFAULT_STAGES,
  ENG_DEFAULT_STAGES,
  TASK_DETAIL_ACTIONS,
  TASK_EXPORT_FIELDS,
  TASK_SUBSTAGES
} from '../global/Environment';
import { TASK_TYPES } from '../constants';

/**
 * @description Get the display name of a task type by formatting the enum value
 * @param {String} type - The task type to get the display name for
 * @returns {String} - The display name of the task type
 */
export const getTypeDisplayName = (type) => {
  return startCase(type.toLowerCase().replaceAll('_', ' '));
};

export const getStageDisplayName = (stage) => {
  switch (stage) {
    case 'Not Started':
      return 'Unlocked';
    case 'eng escalation':
      return 'Eng Escalation';
    case 'user escalation':
      return 'User Escalation';
    default:
      return stage;
  }
};

/**
 * Highlights the matching parts of the text based on the provided search terms.
 * There can be multiple search terms to account for searching "First Last,"
 * which are two separate columns in the table, so we check for matches on each term.
 *
 * @param {string | null} text - The text to be highlighted.
 * @param {string | null} searchTerm - Array of lowercased terms to highlight within the text.
 * @returns {JSX.Element} - A React component with highlighted matching parts.
 */
export const highlightText = (text, searchTerm) => {
  if (!searchTerm || !text) {
    return <span>{text}</span>;
  }

  const lowerSearchTerms = words(searchTerm.toLowerCase());
  const lowerFieldValue = text.toLowerCase();
  const isSearchTermMatch = lowerSearchTerms.some((term) => lowerFieldValue.includes(term));

  // If the search term is not found, return early to avoid unnecessary processing
  if (!isSearchTermMatch) {
    return <span>{text}</span>;
  }

  // Create a case-insensitive regex pattern for each search term, escaping special characters
  const escapedTerms = lowerSearchTerms.map(escapeRegExp);
  const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
  // Split the text into parts based on the search terms
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, index) =>
        lowerSearchTerms.includes(part.toLowerCase()) ? (
          <span key={index} style={{ backgroundColor: 'yellow' }}>
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
};

/**
 * Gets the display value of a task field and highlights matching search terms if applicable.
 *
 * @param {string} field - The field name of the task.
 * @param {Object} task - The task object containing various fields.
 * @param {Object} qdeckUserEmailNameMap - A map of user emails to names.
 * @param {string} searchTerm - The search term used for highlighting.
 * @returns {JSX.Element|string} - The display value of the task field, potentially highlighted.
 */
export const getTaskFieldDisplayVal = (field, task, qdeckUserEmailNameMap, searchTerm) => {
  const fieldsWithNestedObjects = [
    'healthcare_taxpayer',
    'healthcare_spouse',
    'healthcare_dependents',
    'multistate_residency',
    'multistate_address',
    'multistate_income',
    'multistate_expenses'
  ];

  if (fieldsWithNestedObjects.includes(field)) {
    const [colName, nestedField] = field.split('_');
    const fieldVal = get(task, [colName, nestedField]);
    return fieldVal ? JSON.stringify(fieldVal, null, 2) : '';
  }

  if (field === 'type') {
    return getTypeDisplayName(get(task, field));
  }

  if (field === 'stage') {
    return getStageDisplayName(get(task, field));
  }

  if (['updatedAt', 'last_upload', 'last_stage_change'].includes(field) && get(task, field)) {
    return new Date(get(task, field)).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'short'
    });
  }

  if (field === 'assignee' && get(task, field)) {
    return qdeckUserEmailNameMap[get(task, field)];
  }

  if ((field === 'phone' || field === 'email') && task.user) {
    const fieldValue = get(task, ['user', field]);

    return highlightText(fieldValue, searchTerm);
  }

  if (field === 'federal_amount' && task.amounts) {
    return get(task, ['amounts', field]);
  }

  if (field === 'multistate_amount' && task.amounts) {
    return JSON.stringify(get(task, ['amounts', 'multistate']));
  }

  const fieldsToHighlight = ['first_name', 'last_name', 'user_id', 'ssn'];
  if (searchTerm != null && get(task, field) != null && fieldsToHighlight.includes(field)) {
    const fieldValue = get(task, field).toString();
    return highlightText(fieldValue, searchTerm);
  }

  return get(task, field);
};

export const formatTableData = (tasks) => {
  const taskData = tasks.map((task) =>
    TASK_EXPORT_FIELDS.map((field) => {
      if (field === 'phone' || field === 'email') {
        return get(task, ['user', field]) || '';
      }

      if (!get(task, field)) {
        return '';
      }

      if (isObject(get(task, field))) {
        const str = JSON.stringify(get(task, field));
        return str.replace(/"/g, '""');
      }

      return get(task, field);
    })
  );

  const csvData = [TASK_EXPORT_FIELDS, ...taskData];
  return csvData;
};

export const getAvailableActions = (tab) => {
  switch (tab) {
    case 1:
      return [
        'Drake Upload',
        TASK_DETAIL_ACTIONS.UNLOCK_USER,
        TASK_DETAIL_ACTIONS.MOVE_TO_TAX_EXPERT_ESC,
        TASK_DETAIL_ACTIONS.MOVE_TO_ENG_ESC,
        TASK_DETAIL_ACTIONS.DO_NOT_FILE
      ];
    case 2:
      return [
        TASK_DETAIL_ACTIONS.MOVE_TO_OPS_REVIEW,
        TASK_DETAIL_ACTIONS.MOVE_TO_FINAL_REVIEW,
        TASK_DETAIL_ACTIONS.FILED_WITH_IRS,
        TASK_DETAIL_ACTIONS.ACCEPTED_BY_IRS
      ];
    default:
      return [
        TASK_DETAIL_ACTIONS.USER_ESCALATION,
        TASK_DETAIL_ACTIONS.CONFIRM_AMOUNTS,
        TASK_DETAIL_ACTIONS.FILED_WITH_IRS,
        TASK_DETAIL_ACTIONS.ACCEPTED_BY_IRS
      ];
  }
};

export const isActionDisabled = (action, task, user) => {
  const isInactiveTask =
    task.type === TASK_TYPES.STANDARD
      ? some(task.relatedTasks, (relatedTask) => relatedTask?.type === TASK_TYPES.AMENDMENT)
      : false;
  if (isInactiveTask) {
    return true;
  }

  // Handle tasks for deleted users a little differently from normal tasks
  if (!task.user) {
    if (['Accepted by IRS', 'DO NOT FILE'].includes(task.stage)) {
      return true;
    } else if (action === TASK_DETAIL_ACTIONS.DO_NOT_FILE) {
      return false;
    } else if (['Tax Expert Escalations', 'Filed with IRS'].includes(task.stage)) {
      return action !== TASK_DETAIL_ACTIONS.ACCEPTED_BY_IRS;
    } else if (task.stage === 'Final Review') {
      return action !== TASK_DETAIL_ACTIONS.FILED_WITH_IRS;
    }
    return true;
  }

  if (
    task?.stage === 'Tax Expert Escalations' &&
    (action === TASK_DETAIL_ACTIONS.ACCEPTED_BY_IRS ||
      (task.type === TASK_TYPES.AMENDMENT &&
        [
          TASK_DETAIL_ACTIONS.CONFIRM_AMOUNTS,
          TASK_DETAIL_ACTIONS.CONFIRM_AMOUNTS_NO_TEXT_EMAIL,
          TASK_DETAIL_ACTIONS.UPDATE_AMOUNTS
        ].includes(action))) &&
    task.assignee === user.email
  ) {
    return false;
  }

  if (
    task?.stage === 'eng escalation' &&
    action === TASK_DETAIL_ACTIONS.MOVE_TO_TAX_EXPERT_ESC &&
    task.assignee === user.email
  ) {
    return false;
  }

  if (
    task?.stage === 'Accepted by IRS' &&
    action === TASK_DETAIL_ACTIONS.MOVE_TO_TAX_EXPERT_ESC &&
    task.assignee === user.email
  ) {
    return false;
  }

  if (task.type === TASK_TYPES.AMENDMENT && action === TASK_DETAIL_ACTIONS.MOVE_TO_OPS_REVIEW) {
    return true;
  }

  if (!task.assignee || task.assignee !== user.email) {
    return true;
  }

  const blocked = TASK_SUBSTAGES.BLOCKED in task.substagesMap;
  const emailOutbox = TASK_SUBSTAGES.EMAIL_OUTBOX in task.substagesMap;
  if (blocked || emailOutbox) {
    return true;
  }

  return ACTION_STAGE_MAP[action] ? !ACTION_STAGE_MAP[action].includes(task.stage.toLowerCase()) : true;
};

export const getActionConfirmationMsg = (action) => {
  switch (action) {
    case TASK_DETAIL_ACTIONS.UPLOAD_TAX_DATA:
    case TASK_DETAIL_ACTIONS.UPLOAD_DEPOSIT_WITHDRAWAL_FORMS:
    case TASK_DETAIL_ACTIONS.UNLOCK_USER:
    case TASK_DETAIL_ACTIONS.MOVE_TO_TAX_EXPERT_ESC:
    case TASK_DETAIL_ACTIONS.MOVE_TO_OPS_REVIEW:
    case TASK_DETAIL_ACTIONS.MOVE_TO_FINAL_REVIEW:
    case TASK_DETAIL_ACTIONS.MOVE_TO_ENG_ESC:
      return action;

    case TASK_DETAIL_ACTIONS.FILED_WITH_IRS:
    case TASK_DETAIL_ACTIONS.ACCEPTED_BY_IRS:
    case TASK_DETAIL_ACTIONS.DO_NOT_FILE:
      return `Move to ${action}`;

    case TASK_DETAIL_ACTIONS.CONFIRM_AMOUNTS:
      return 'Move to Confirm Amounts and notify user';

    case TASK_DETAIL_ACTIONS.CONFIRM_AMOUNTS_NO_TEXT_EMAIL:
      return 'Move to Confirm Amounts without notifying user';

    case TASK_DETAIL_ACTIONS.UPDATE_AMOUNTS:
      return 'Run Drake amounts job';

    case TASK_SUBSTAGES.BLOCKED:
      return 'Update substage to Blocked and email user';

    case TASK_SUBSTAGES.EMAIL_OUTBOX:
      return 'Update substage to Email Outbox';

    case TASK_SUBSTAGES.PAST_YEAR_RETURN:
      return 'Update substage to Past Year Return';

    default:
      return '';
  }
};

export const formatActivityLog = (records, qdeckUserEmailNameMap) => {
  const formatRecord = (record) => {
    const utcTimestamp = new Date(record.createdAt);
    const date = utcTimestamp.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      dateStyle: 'medium'
    });
    const time = utcTimestamp.toLocaleTimeString('en-US', {
      timeZone: 'America/Los_Angeles',
      timeStyle: 'long'
    });

    let text = record.activity;
    if (text.startsWith('<br>')) {
      const updateSource = qdeckUserEmailNameMap[record.source] || record.source;
      text = `<b>${updateSource}</b> updated the following fields:${text}`;
    }

    return { date, time, activity: text };
  };
  const formattedRecords = orderBy(records, (record) => new Date(record.createdAt), 'desc').map(formatRecord);
  const groupedRecords = groupBy(formattedRecords, 'date');
  const orderedGroups = orderBy(Object.entries(groupedRecords), ([dateKey]) => new Date(dateKey), 'desc');
  return Object.fromEntries(orderedGroups);
};

export const formatComments = (records, qdeckUserEmailNameMap) => {
  return orderBy(records, (record) => new Date(record.createdAt), 'desc').map((record) => {
    const utcTimestamp = new Date(record.createdAt);

    return {
      ...record,
      authorName: qdeckUserEmailNameMap[record.authorEmail] || record.authorName,
      createdAt: utcTimestamp.toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        dateStyle: 'short',
        timeStyle: 'long'
      })
    };
  });
};

export const formatAskLog = (records, qdeckUserEmailNameMap) => {
  const sortedMessages = orderBy(records, (r) => new Date(r.time), 'desc').slice(-200);

  return sortedMessages.map((record) => {
    const utcTimestamp = new Date(record.time);

    return {
      ...record,
      authorName: qdeckUserEmailNameMap[record.authorEmail] || record.authorName,
      time: utcTimestamp.toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        dateStyle: 'short',
        timeStyle: 'long'
      })
    };
  });
};

export const formatUserEscLog = (records) => {
  const formattedRecords = records.map((record) => {
    const utcTimestamp = new Date(record.createdAt);
    const date = utcTimestamp.toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      dateStyle: 'medium'
    });
    const time = utcTimestamp.toLocaleTimeString('en-US', {
      timeZone: 'America/Los_Angeles',
      timeStyle: 'long'
    });

    return {
      ...record,
      date,
      time
    };
  });

  // Group records by date and ensure all records for each date are sorted by time (descending)
  const groupedRecords = mapValues(groupBy(formattedRecords, 'date'), (records) => orderBy(records, 'time', 'desc'));

  return groupedRecords;
};

/**
 * Checks if the current user is an engineer.
 *
 * @param {string} email - The email of the current user.
 * @param {Object[]} users - An array of user objects.
 * @returns {boolean} - True if the user is an engineer, false otherwise
 */
export const isCurrentUserEng = (email, users) => some(users, { email, eng: true });

/**
 * Checks if the current user is an admin.
 *
 * @param {string} email - The email of the current user.
 * @param {Object[]} users - An array of user objects.
 * @returns {boolean} - True if the user is an admin, false otherwise
 */
export const isCurrentUserAdmin = (email, users) => some(users, { email, admin: true });

export const prepareUserDropdownList = (qdeckUserEmailNameMap) => {
  return sortBy(
    Object.entries(qdeckUserEmailNameMap).map(([email, name]) =>
      email === 'placeholder' ? { label: '', value: '' } : { label: name, value: email }
    ),
    'label'
  );
};

/**
 * Gets the stage options for the task list
 */
export const getStageOptions = (showAllReturns, isEng) => {
  if (showAllReturns) {
    return ALL_STAGES;
  } else if (isEng) {
    return ENG_DEFAULT_STAGES;
  } else {
    return DEFAULT_STAGES;
  }
};
